import {Injectable} from '@angular/core';
import {AppConfigEnvironment} from '@shared/models/appconfig-environment.model';
import {Observable, Subject} from 'rxjs';
import {AppConfigEnvironmentService} from '@shared/service/appconfig-environment.service';
import {MatSnackBar, MatSnackBarConfig} from '@angular/material';
import {Utilities} from '@shared/utilities/utilities.model';
import $ from 'jquery/dist/jquery.min';
import {
	CommonMessageCode,
	MessageComponentType,
	MessageDetail,
	MessageModel,
	MessageType,
	SnackBarClasses
} from '@shared/service/message-manager/message.model';
import {MessageManagerUtilities} from '@shared/service/message-manager/message-manager-utilities';
import {PermissionService} from '@shared/service/permission.service';
import {UserPermissionsModel} from '@shared/models/user-permission.model';
import {Module, ModuleService} from '@shared/service/module.service';
import {InnerMessageAuthorityMessageCode} from '../../../authority/authority-shared/services/inner-message-authority-utilities';


@Injectable()
export class MessageManagerService {

	environment: AppConfigEnvironment;
	language: string;
	messages: { [key: string]: { [key: string]: string } } = {};
	userModulesIds: string[] = [];
	contextConfig: { [key: string]: string };

	protected pushInnerMessagesSubject: Subject<MessageModel> = new Subject<MessageModel>();
	pushInnerMessagesObservable: Observable<MessageModel> = this.pushInnerMessagesSubject.asObservable();

	protected removeInnerMessagesSubject: Subject<string> = new Subject<string>();
	removeInnerMessagesObservable: Observable<string> = this.removeInnerMessagesSubject.asObservable();

	private snackBarDelay: number = 5000;

	constructor(private appConfig: AppConfigEnvironmentService,
				private snackBarService: MatSnackBar,
				private permissionService: PermissionService,
				private moduleService: ModuleService) {
		this.environment = this.appConfig.Environment;
		this.language = this.environment.SERVER_DATE_LOCATION;
		const permissions: UserPermissionsModel = this.permissionService.getCurrentUserPermissions();
		permissions.profiles.forEach(profile => this.userModulesIds.push(profile.module.key));
		/** Loading messages from available modules*/
		const contexts: string[] = this.onSetContexts(this.userModulesIds);
		contexts.forEach(context => {
			try {
				this.messages[context] = require(`./messages/${context}/${context}-messages.${this.language}.json`);
			} catch (e) {
				this.messages[context] = null;
			}
		});
	
		// Per Mobility bisogna caricare anche i messaggi di Authority
		// questo serve per non avere una copia di Authority nei messaggi di Mobility
		if (contexts.includes(Module.MOBILITY)) {
			const ctx = Module.AUTHORITY;
			this.messages[ctx] = require(`./messages/${ctx}/${ctx}-messages.${this.language}.json`);
		}

		/**Always loading cross-permission messages*/
		this.messages['cross-permission'] = require(`./messages/cross-permission/cross-permission-messages.${this.language}.json`);
		this.messages['notification-center'] = require(`./messages/notification-center/notification-center-messages.${this.language}.json`);
		this.messages['shared'] = require(`./messages/shared/shared-messages.${this.language}.json`);
	}

	onSetContexts(ids: string[]): string[] {
		this.contextConfig = require('./messages/contextConfig.json');
		const modulesArray: string[] = ids.map(id => this.contextConfig[id]);
		return modulesArray.filter((item, pos, self) => self.indexOf(item) === pos);
	}


	/**
	 * the method that you have to invoke to display a message,
	 *  @param messageDetail, required, it's the object that contains the specific for the message - {@link MessageDetail}
	 *  @param module, optional, if you want to take the code from a different module - {@link Module}
	 *
	 *  the method also check if you have some messages that is different for profile, by concat to the
	 *  message code the profile type
	 *  example -->'GENERIC_ERROR_ALD'
	 *
	 *  {@link messageToPush}
	 *
	 *  if you want to add new profile for a specific module see below {@link PermissionService#profileType}
	 * */

	pushInnerMessage<T>(messageDetail: MessageDetail<T>, module?: Module): void {

		module = !!module ? module : this.moduleService.getCurrentModule();
		
		// Mobility si appoggia sugli errori di Authority
		// per evitare un copia e incolla massivo
		// verranno associati gli errori di Authority in modo forzato
		module = (module === Module.MOBILITY) ? Module.AUTHORITY : module;
		
		const message = this.messageToPush(messageDetail, module);

		// TODO - possibili sviluppi successivi
		// considerare il caso in cui ci siano errori solo di mobility
		// e fare un merge dei 2 JSON
		// va inserito il controllo dentro messageToPush()


		// ternario ridondante
		// const message: MessageModel = !!module ? this.messageToPush(messageDetail, module) :
		// 	this.messageToPush(messageDetail, this.moduleService.getCurrentModule());
		// versione più short
		// const message = this.messageToPush(messageDetail, !!module ? module : this.moduleService.getCurrentModule());


		setTimeout(() => {
			switch (messageDetail.componentType) {
				case MessageComponentType.FLASH_MESSAGE:

					if (this.permissionService.getCurrentUserPermissions().isSuperUserByLocationPath(module) &&
						messageDetail.code === InnerMessageAuthorityMessageCode.GATEWAY_1002) {
						message.type = MessageType.TYPE_WARNING;
					}

					this.pushInnerMessagesSubject.next(message);
					if (messageDetail.isTop !== false) {
						Utilities.scrollUp($);
					}
					break;
				case MessageComponentType.SNACK_BAR:
					let snackBarClass: string = null;
					switch (messageDetail.type) {
						case MessageType.TYPE_WARNING:
							snackBarClass = SnackBarClasses.WARNING;
							break;
						case MessageType.TYPE_SUCCESS:
							snackBarClass = SnackBarClasses.SUCCESS;
							break;
						case MessageType.TYPE_INFO:
							snackBarClass = SnackBarClasses.INFO;
							break;
						case MessageType.TYPE_ERROR:
							snackBarClass = SnackBarClasses.ERROR;
							break;
					}
					const snackBarConfig: MatSnackBarConfig = new MatSnackBarConfig();
					snackBarConfig.panelClass = snackBarClass;
					snackBarConfig.duration = this.snackBarDelay;

					this.snackBarService.open(message.id, '', snackBarConfig);
					break;
			}
		}, 0);
	}

	createMessageError(codeError:string): MessageDetail{
		let errorMessage:MessageDetail;
		errorMessage =  {
			code: codeError,
			type: MessageType.TYPE_ERROR,
			componentType: MessageComponentType.FLASH_MESSAGE
		};
		return errorMessage;
	}

	removeMessage(idInnerMessage: string): void {
		this.removeInnerMessagesSubject.next(idInnerMessage);
	}

	deleteAll(): void {
		this.removeInnerMessagesSubject.next(CommonMessageCode.DELETE_ALL);
	}

	private messageToPush<T>(messageDetail: MessageDetail<T>, messageContext: Module): MessageModel {

		// caso d'uso in cui non c'è il codice, ma solo il messaggio custom
		if (!messageDetail.code && messageDetail.message) {
			const mess = MessageManagerUtilities.getMessage<T>(CommonMessageCode.EMPTY_MESSAGE, null, messageDetail.message);
			return new MessageModel(mess, messageDetail.type, messageDetail.enableAutoDelete, messageDetail.componentType, messageDetail.isTop, messageDetail.keepAlive, messageDetail.delay);
		}

		let mess: string;
		if (!!this.messages[messageContext]) {

			if (!!this.messages[messageContext][`${messageDetail.code}_${this.permissionService.profileType}`]) {
				messageDetail.code = `${messageDetail.code}_${this.permissionService.profileType}`;
			}

			if (messageDetail.code) {
				mess = messageDetail.message ? MessageManagerUtilities.getMessage<T>(messageDetail.code, this.messages[messageContext], messageDetail.message) :
					MessageManagerUtilities.getMessage<T>(messageDetail.code, this.messages[messageContext]);
			} else {
				messageDetail.code = messageDetail.type === MessageType.TYPE_SUCCESS ? CommonMessageCode.GENERIC_SUCCESS : CommonMessageCode.GENERIC_ERROR;
				mess = MessageManagerUtilities.getMessage<T>(messageDetail.code, this.messages[messageContext]);
			}

		} else {
			mess = MessageManagerUtilities.getMessage<T>(CommonMessageCode.EMPTY_MESSAGE, null);

			return new MessageModel(mess, MessageType.TYPE_WARNING, messageDetail.enableAutoDelete, messageDetail.componentType, messageDetail.isTop, messageDetail.keepAlive, messageDetail.delay);
		}
		return new MessageModel(mess, messageDetail.type, messageDetail.enableAutoDelete, messageDetail.componentType, messageDetail.isTop, messageDetail.keepAlive, messageDetail.delay);
	}
}
